import { oc } from "@orpc/contract";
import { z } from "zod";

/**
 * Sandbox instance lifecycle + observability — the primitive a remote consumer
 * (the Python `ai-service`) calls to provision/tear down sandboxes and to read
 * their trace. The whole `sandboxes.*` namespace is authenticated by the shared
 * sandbox service token (see the `/sandboxes/` gate in the RPC express adapter),
 * not a user session, so the caller declares its `organizationId` on every call.
 * A future
 * user-facing admin view must NOT reuse these as-is — it would need to derive
 * org from the session, since a session caller could otherwise pass any org id.
 *
 * Execution stays at the consumer (Daytona SDK, direct) — Node never proxies
 * exec. For the trace to populate, the consumer reports each exec back via
 * `recordExec` (Node's in-process `code_sandbox` step does the same inline).
 */

// --- Shared output shapes (reused by the workflow-run trace endpoints) -------

export const sandboxInstanceSchema = z.object({
  id: z.string(),
  daytonaSandboxId: z.string(),
  status: z.string(),
  configId: z.string().nullable(),
  runId: z.string().nullable(),
  traceId: z.string().nullable(),
  workflowStepId: z.string().nullable(),
  createdAt: z.string(),
  lastActiveAt: z.string().nullable(),
});

export const sandboxSpanSchema = z.object({
  spanId: z.string(),
  parentSpanId: z.string().nullable(),
  name: z.string(),
  kind: z.string(),
  startedAt: z.string(),
  endedAt: z.string().nullable(),
  durationMs: z.number().nullable(),
  status: z.string(),
  exitCode: z.number().nullable(),
  attributes: z.record(z.string(), z.unknown()),
});

export const sandboxLogLineSchema = z.object({
  ts: z.string(),
  stream: z.string(),
  message: z.string().nullable(),
  spanId: z.string().nullable(),
});

const spanKindSchema = z.enum(["exec", "code_run", "fs", "http", "other"]);

const instanceStatusSchema = z.enum([
  "provisioning",
  "started",
  "stopped",
  "archived",
  "deleted",
  "error",
]);

// --- Procedures --------------------------------------------------------------

const createContract = oc
  .input(
    z.object({
      organizationId: z.string().uuid(),
      // Daytona image reference, e.g. "python:3.12-slim". Required: a stored
      // config carries network/lifecycle/env but never an image.
      image: z.string().min(1),
      // Optional stored config — pulls network/lifecycle/labels/envVars.
      configId: z.string().uuid().optional(),
      envVars: z.record(z.string(), z.string()).optional(),
      resources: z
        .object({
          cpu: z.number().int().min(1),
          memory: z.number().int().min(1),
          disk: z.number().int().min(1),
        })
        .optional(),
      labels: z.record(z.string(), z.string()).optional(),
      // A remote consumer may tighten a stored config to fully deny network
      // access. Literal true is intentional: this service-token surface must
      // never be able to weaken an org-owned sandbox config.
      networkBlockAll: z.literal(true).optional(),
      // Correlation metadata, all optional — ties the instance to a caller run.
      runId: z.string().optional(),
      traceId: z.string().optional(),
      userId: z.string().uuid().optional(),
    }),
  )
  .output(sandboxInstanceSchema)
  .route({
    summary: "Provision a sandbox",
    description:
      "Starts a code-execution sandbox for an org from an image, with optional stored config, env vars, resources, labels, network lockdown and run correlation ids. Service-token only.",
  });

const getContract = oc
  .input(
    z.object({
      id: z.string().uuid(),
      organizationId: z.string().uuid(),
      includeDeleted: z.boolean().optional(),
    }),
  )
  .output(
    z.object({
      sandbox: sandboxInstanceSchema.nullable(),
      spans: z.array(sandboxSpanSchema),
    }),
  )
  .route({
    method: "GET",
    summary: "Get a sandbox + its trace",
    description:
      "Returns one org sandbox instance and its trace spans (execs, code runs, file and network operations). Service-token only.",
  });

const listContract = oc
  .input(
    z.object({
      organizationId: z.string().uuid(),
      status: instanceStatusSchema.optional(),
      runId: z.string().optional(),
      createdBy: z.string().uuid().optional(),
      includeDeleted: z.boolean().optional(),
    }),
  )
  .output(z.array(sandboxInstanceSchema))
  .route({
    method: "GET",
    summary: "List an org's sandboxes",
    description:
      "Lists an org's code-execution sandboxes, filterable by status, run id and creator, optionally including deleted ones. Service-token only.",
  });

const refreshContract = oc
  .input(z.object({ id: z.string().uuid(), organizationId: z.string().uuid() }))
  .output(sandboxInstanceSchema.nullable())
  .route({
    summary: "Touch activity + reconcile status (lease heartbeat)",
    description:
      "Marks a sandbox as recently active and syncs its status from the sandbox provider; returns the updated instance. Service-token only.",
  });

const deleteContract = oc
  .input(z.object({ id: z.string().uuid(), organizationId: z.string().uuid() }))
  .output(z.object({ success: z.boolean() }))
  .route({
    summary: "Tear down a sandbox",
    description:
      "Destroys a code-execution sandbox at the provider and marks it deleted. Service-token only.",
  });

const getLogsContract = oc
  .input(
    z.object({
      id: z.string().uuid(),
      organizationId: z.string().uuid(),
      includeDeleted: z.boolean().optional(),
    }),
  )
  .output(
    z.object({
      lines: z.array(sandboxLogLineSchema),
      truncated: z.boolean(),
    }),
  )
  .route({
    method: "GET",
    summary: "Get a sandbox's logs",
    description:
      "Returns a sandbox's stdout/stderr log lines with timestamps and span ids, flagging when the output was truncated. Service-token only.",
  });

const recordExecContract = oc
  .input(
    z.object({
      id: z.string().uuid(),
      organizationId: z.string().uuid(),
      name: z.string().min(1),
      kind: spanKindSchema,
      startedAt: z.string().datetime(),
      endedAt: z.string().datetime(),
      exitCode: z.number().int().nullable(),
      stdout: z.string().nullable().optional(),
      stderr: z.string().nullable().optional(),
      // Remote SDK consumers can pre-allocate ids to build a trace tree while
      // the parent operation is still running. Existing callers may omit both.
      spanId: z.string().uuid().optional(),
      parentSpanId: z.string().uuid().optional(),
      attributes: z
        .record(
          z.string(),
          z.union([z.string(), z.number(), z.boolean(), z.null()]),
        )
        .optional(),
    }),
  )
  .output(z.object({ success: z.boolean() }))
  .route({
    summary: "Report an exec the consumer ran (feeds the trace)",
    description:
      "Records one command or code run executed in a sandbox (timing, exit code, stdout/stderr, attributes) as a span in its trace. Service-token only.",
  });

export const sandboxesContracts = {
  create: createContract,
  get: getContract,
  list: listContract,
  refresh: refreshContract,
  delete: deleteContract,
  getLogs: getLogsContract,
  recordExec: recordExecContract,
};
